"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, UserCircle2, Video, ArrowRight, Plus, X } from "lucide-react";
import { FarminstaIntroLayout } from "@/app/home/landing-page";
import AnimatedSection from "../home/animated-section";
import { ScrollingLogosComponent } from "../home/scrolling-logos";
import Image from "next/image";
import img2 from '../../public/images/WebsiteImages/dcm/2.svg'


// Green accent color (matching Field Force Monitor)
const GREEN = "#22A850";
//bg-[#ebf2f0]

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <section className="relative pt-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-[#ebf2f0]">
      <div className="absolute top-[-100px] right-[-200px] w-[600px] h-[600px] pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center relative">
          {/* Left Content */}
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1612] mb-7 leading-tight tracking-tight">
              Get paid to make content for{" "}
              <em className="italic font-medium text-[#22A850]">India's farmers</em>
            </h1>

            <p className="text-lg sm:text-xl text-[#3D362F] leading-relaxed mb-10 max-w-xl">
              Farminsta is the only short-video platform built for agriculture. Post reels, reach farmers across 600,000 villages, and get hired by the agri brands that already run their seasons on Farminsta.
            </p>

            {/* <Button 
              size="lg"
              style={{ backgroundColor: GREEN }}
              className="hover:opacity-90 text-white px-7 py-6 text-base font-semibold rounded-lg transition-all"
              onClick={() => window.open("https://app.farminsta.com/", "_blank")}
            >
              Download the app
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button> */}
          </div>

          {/* Right - Creator Illustration */}
          <div className="relative w-full max-w-[500px] h-[500px] mx-auto lg:ml-auto">
              <div className="mt-10 lg:mt-0">
                          <Image
                            className="w-full h-auto max-w-md mx-auto"
                            src={img2}
                            alt="Influencer connection illustration"
                          />
                        </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Feature Cards Section
const FeatureCardsSection: React.FC = () => {
  return (
    <section className="py-6 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-[#E3DAD0] rounded-xl p-8 hover:-translate-y-1 hover:border-[#22A850] transition-all duration-300">
            <h3 className="text-xl font-semibold text-[#1A1612] mb-3 leading-tight">
              Regular payouts, no follower cap
            </h3>
            <p className="text-sm text-[#3D362F] leading-relaxed">
              Earn from day one. Points convert to bank transfer after KYC — no minimum subscriber gate, no algorithmic guesswork.
            </p>
          </div>

          <div className="bg-white border border-[#E3DAD0] rounded-xl p-8 hover:-translate-y-1 hover:border-[#22A850] transition-all duration-300">
            <h3 className="text-xl font-semibold text-[#1A1612] mb-3 leading-tight">
              Hired by India's top agri brands
            </h3>
            <p className="text-sm text-[#3D362F] leading-relaxed">
              Mahindra, Namdeo, Super Seeds, United Genetics and 40+ agri companies run sponsored content through Farminsta creators.
            </p>
          </div>

          <div className="bg-white border border-[#E3DAD0] rounded-xl p-8 hover:-translate-y-1 hover:border-[#22A850] transition-all duration-300">
            <h3 className="text-xl font-semibold text-[#1A1612] mb-3 leading-tight">
              Real feedback from real farmers
            </h3>
            <p className="text-sm text-[#3D362F] leading-relaxed">
              Every reel reaches a farmer audience — not a generic feed. Comments, questions, and demo requests come back to you from the field.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  return (
    <section className="py-8 px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612]leading-tight tracking-tight text-center">
          Three steps to your <em className="italic text-[#22A850] font-normal">first sponsored reel</em>
        </h2>

        <div className="grid md:grid-cols-3 gap-10 mt-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#22A850] rounded-full flex items-center justify-center mx-auto mb-6">
              <Download className="w-8 h-8 text-white" />
            </div>
            {/* <div className="text-5xl font-normal text-[#22A850] mb-4">1</div> */}
            <h3 className="text-2xl font-semibold text-[#1A1612] mb-3">Download Farminsta</h3>
            <p className="text-base text-[#3D362F] leading-relaxed">
              Available on Play Store and App Store. Create your account in under 2 minutes.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-[#22A850] rounded-full flex items-center justify-center mx-auto mb-6">
              <UserCircle2 className="w-8 h-8 text-white" />
            </div>
            {/* <div className="text-5xl font-normal text-[#22A850] mb-4">2</div> */}
            <h3 className="text-2xl font-semibold text-[#1A1612] mb-3">Set up your creator profile</h3>
            <p className="text-base text-[#3D362F] leading-relaxed">
              Tell us your crop expertise, language, and region. We match you to brands that need your audience.
            </p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-[#22A850] rounded-full flex items-center justify-center mx-auto mb-6">
              <Video className="w-8 h-8 text-white" />
            </div>
            {/* <div className="text-5xl font-normal text-[#22A850] mb-4">3</div> */}
            <h3 className="text-2xl font-semibold text-[#1A1612] mb-3">Post reels, earn points</h3>
            <p className="text-base text-[#3D362F] leading-relaxed">
              Every view, like, and farmer interaction earns points. Points convert to cash. Brands hire directly through the app.
            </p>
          </div>
        </div>

        <div className="text-center ">
          <Button 
            size="lg"
            style={{ backgroundColor: GREEN }}
            className="hover:opacity-90 text-white px-8 py-6 text-base font-semibold rounded-lg transition-all"
            onClick={() => window.open("https://app.farminsta.com/", "_blank")}
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Brands Hiring Section
const BrandsHiringSection: React.FC = () => {
  return (
    <section className="pt-6 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="text-center">
          <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-2 leading-tight tracking-tight">
            Brands hiring on <em className="italic text-[#22A850] font-normal">Farminsta</em>
          </h2>
          <p className="text-lg text-[#7A6E62] leading-relaxed mb-6">
            42+ agri brands have run campaigns with Farminsta creators. Your reel could be next.
          </p>
        </div>
        <ScrollingLogosComponent />
      </div>
    </section>
  );
};

// Creator Testimonials Section
const TestimonialsSection: React.FC = () => {
  const testimonials = [
    {
      quote: "Farminsta is built for agriculture, not against it. Farmers actually watch, comment, and ask questions — that feedback has made my videos sharper every week.",
      name: "Manasa",
      location: "Amalapuram, Andhra Pradesh"
    },
    {
      quote: "Sponsorship projects on Farminsta are simple to start. I've worked with leading agri brands without needing an agent or a manager.",
      name: "Rahul",
      location: "KGF, Karnataka"
    },
    {
      quote: "On other platforms I was competing with everyone. On Farminsta I'm competing only on what I know best — farming. It's the right launchpad for an agri creator.",
      name: "Pavan",
      location: "Nellore, Andhra Pradesh"
    }
  ];

  return (
    <section className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612]leading-tight tracking-tight text-center">
          From first reel to <em className="italic text-[#22A850] font-normal">full-time creator</em>
        </h2>

        <div className="grid md:grid-cols-3 gap-8 mt-6">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white border border-[#E3DAD0] rounded-xl p-8 hover:-translate-y-1 hover:border-[#22A850] transition-all duration-300 flex flex-col"
            >
              <p className="text-base text-[#3D362F] leading-relaxed mb-6 italic flex-grow">
                "{testimonial.quote}"
              </p>
              <div className="pt-4 border-t border-[#E3DAD0]">
                <p className="font-semibold text-[#1A1612]">{testimonial.name}</p>
                <p className="text-sm text-[#7A6E62]">{testimonial.location}</p>
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-8 text-center">
          <p className="text-sm italic text-[#7A6E62]">
            * Testimonials rewritten for clarity while preserving creator voice and meaning
          </p>
        </div> */}
      </div>
    </section>
  );
};

// Content Guidance Section
const ContentGuidanceSection: React.FC = () => {
  const contentTypes = [
    "How-to and solutions — pest control, irrigation, input application techniques",
    "Product reviews — honest takes on seeds, agrochemicals, equipment",
    "Farm tours — your operation, your crops, your decisions",
    "Expert interviews — agronomists, scientists, experienced farmers",
    "Humour and culture — farming life, village stories, festivals"
  ];

  return (
    <section className="py-6 px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-2 leading-tight tracking-tight">
          Your content, <em className="italic text-[#22A850] font-normal">your call</em>
        </h2>

        <p className="text-lg text-[#3D362F] leading-relaxed mb-2">
          Farminsta creators post:
        </p>

        <ul className="space-y-4 mb-4">
          {contentTypes.map((type, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="w-2 h-2 bg-[#22A850] rounded-full mt-2 flex-shrink-0"></span>
              <span className="text-base text-[#3D362F] leading-relaxed">{type}</span>
            </li>
          ))}
        </ul>

        <p className="text-lg text-[#1A1612] font-medium leading-relaxed">
          The only rule: it has to be useful or entertaining to a farmer. Beyond that, surprise us.
        </p>
      </div>
    </section>
  );
};

// Earnings Section
const EarningsSection: React.FC = () => {
  const earningStreams = [
    {
      num: "1",
      title: "Engagement points",
      description: "Every view, like, comment, share, and farmer-to-expert connection earns points. Points convert directly to bank transfer after KYC. No cap, no minimum follower threshold."
    },
    {
      num: "2",
      title: "Sponsored brand campaigns",
      description: "Agri brands hire Farminsta creators for sponsored reels, product launches, and seasonal campaigns. You set your rate as you grow."
    },
    {
      num: "3",
      title: "Long-term brand partnerships",
      description: "Creators who consistently engage a regional or crop-specific audience get repeat work — sometimes as ongoing brand ambassadors for a full season."
    }
  ];

  return (
    <section className="py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-2 leading-tight tracking-tight">
          How creators <em className="italic text-[#22A850] font-normal">get paid</em> on Farminsta
        </h2>

        <p className="text-lg text-[#7A6E62] leading-relaxed mb-4">
          Three earning streams stack on every Farminsta creator account.
        </p>

        <div className="space-y-0">
          {earningStreams.map((stream, index) => (
            <div 
              key={index}
              className="grid md:grid-cols-[80px_1fr] gap-2 items-start py-4 border-t border-[#E3DAD0] last:border-b"
            >
              <div className="text-6xl font-normal text-[#22A850] leading-none">
                {stream.num}
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-[#1A1612] mb-3 leading-tight tracking-tight">
                  {stream.title}
                </h3>
                <p className="text-base text-[#3D362F] leading-relaxed">
                  {stream.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Item Component
// const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
//   const [isOpen, setIsOpen] = useState(false);

//   return (
//     <div className="border-b border-[#E3DAD0] py-5">
//       <button
//         className="flex items-start gap-3 w-full text-left"
//         onClick={() => setIsOpen(!isOpen)}
//       >
//         <span className="text-[#22A850] mt-1 flex-shrink-0">
//           {isOpen ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
//         </span>
//         <span className="text-lg font-semibold text-[#1A1612]">{question}</span>
//       </button>
//       <div
//         className={`overflow-hidden transition-all duration-300 ease-in-out ${
//           isOpen ? "max-h-[500px] opacity-100 mt-3" : "max-h-0 opacity-0"
//         }`}
//       >
//         <p className="text-base text-[#3D362F] leading-relaxed pl-8 whitespace-pre-line">
//           {answer}
//         </p>
//       </div>
//     </div>
//   );
// };

// FAQ Section
// const FAQSection: React.FC = () => {
//   const faqs = [
//     {
//       question: "What makes Farminsta different from YouTube or Instagram?",
//       answer: "Farminsta is built for one audience — farmers — and works with one industry: agri inputs and equipment. That means every view counts more, and brand partnerships are easier to land because the brands are already on the platform."
//     },
//     {
//       question: "Who can join Farminsta as a creator?",
//       answer: "Anyone passionate about agriculture and willing to make content for farmers. Nano-creators, micro-influencers, agronomists, farm owners, and established agri YouTubers all earn on Farminsta. Follower count doesn't gate the platform."
//     },
//     {
//       question: "How do I sign up?",
//       answer: "Download the Farminsta app from Play Store or App Store, create your profile, and start posting. You can begin earning engagement points from your first reel."
//     },
//     {
//       question: "How do points convert to cash?",
//       answer: "Complete KYC verification once, then redeem points as direct bank transfer after crossing the minimum threshold."
//     },
//     {
//       question: "Is there a cap on earnings?",
//       answer: "No. Earnings scale with the value your content brings to farmers — quality of engagement, farmer-to-expert connections you trigger, brand campaigns you land. No ceiling."
//     },
//     {
//       question: "How do I get hired by brands?",
//       answer: "Active Farminsta creators show up in the brand campaign hub. As you build engaged farmer audiences in specific crops or regions, brand campaign managers reach out directly through the app for sponsored content."
//     },
//     {
//       question: "Can I track my performance?",
//       answer: "Yes. The Farminsta app gives you engagement metrics, follower growth, post reach, and points earned — refreshed daily."
//     },
//     {
//       question: "How do I grow my audience faster?",
//       answer: "Share your Farminsta referral code on your other channels (Instagram, YouTube, ShareChat, WhatsApp). Use crop-specific and region-specific hashtags. Reply to farmer comments — the algorithm rewards interaction, not just views."
//     },
//     {
//       question: "What kind of content works best?",
//       answer: "Solutions-led content (how to deal with this pest, how to improve this yield) consistently performs strongest with farmer audiences. Product reviews, regional crop-specific tips, and honest farm tours also rank well."
//     }
//   ];

//   return (
//     <section className="pt-10 px-4 sm:px-6 lg:px-8 bg-[#ebf2f0]">
//       <div className="max-w-7xl mx-auto">
//         <h2 className="text-4xl sm:text-5xl font-medium text-[#1A1612] mb-4 leading-tight tracking-tight">
//           Frequently Asked Questions
//         </h2>

//         <div className="space-y-2">
//           {faqs.map((faq, index) => (
//             <FAQItem key={index} question={faq.question} answer={faq.answer} />
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// Final CTA Section
const FinalCTASection: React.FC = () => {
  return (
    <section className="py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <h4 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1612] mb-2 leading-tight tracking-tight">
          Ready to make your <em className="italic text-[#22A850] font-normal">first reel?</em>
        </h4>
        <p className="text-xl text-[#3D362F] leading-relaxed mb-4">
          Join thousands of agri creators earning on Farminsta.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <Button 
            size="lg"
            style={{ backgroundColor: GREEN }}
            className="hover:opacity-90 text-white px-8 py-6 text-base font-semibold rounded-lg transition-all"
            onClick={() => window.open("https://app.farminsta.com/", "_blank")}
          >
            Google Play
          </Button>
          <Button 
            size="lg"
            variant="outline"
            style={{ borderColor: GREEN, color: GREEN }}
            className="px-8 py-6 text-base font-semibold border-2 rounded-lg transition-all"
            onClick={() => window.open("https://app.farminsta.com/", "_blank")}
          >
            App Store
          </Button>
        </div>
      </div>
    </section>
  );
};

// Main Page Component
export default function InfluencerPage() {
  return (
    <FarminstaIntroLayout currentPage="influencer">
      <HeroSection />
      <AnimatedSection>
        <FeatureCardsSection />
      </AnimatedSection>
      <AnimatedSection>
        <HowItWorksSection />
      </AnimatedSection>
      <AnimatedSection>
        <BrandsHiringSection />
      </AnimatedSection>
      <AnimatedSection>
        <TestimonialsSection />
      </AnimatedSection>
      <AnimatedSection>
        <ContentGuidanceSection />
      </AnimatedSection>
      <AnimatedSection>
        <EarningsSection />
      </AnimatedSection>
      {/* <AnimatedSection>
        <FAQSection />
      </AnimatedSection> */}
      <AnimatedSection>
        <FinalCTASection />
      </AnimatedSection>
    </FarminstaIntroLayout>
  );
}
